<template>
  <div class="h-full min-h-screen overflow-scroll bg-gray-900">
    <!-- Main centered container -->
    <div
      class="flex h-full min-h-[calc(100vh-80px)] flex-col items-start transition-all delay-100 duration-300 ease-in-out">
      <Header />
      <div class="flex w-full flex-1 py-6">
        <slot />
      </div>
    </div>

    <!-- Create Workspace Dialog -->
  </div>
</template>
<script setup>
import { Header } from '@/components';
import { useAppAction } from '@/composables/useAppAction';
import { useAssetStore } from '@/stores/api/asset';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { useUserStore } from '@/stores/userStore';
import { onMounted, ref, watch } from 'vue';

const workspaceStore = useWorkspaceStore();
const assetStore = useAssetStore();
const userStore = useUserStore();
const selectedWorkspaceId = ref(localStorage.getItem('crux-selected-workspace-id') || null);
const workspacesLoading = ref(true);
const workspacesError = ref(null);
const assetsError = ref(null);

const { handleApiResponse } = useAppAction();
// Methods
const loadWorkspaces = async () => {
  // Check if user is authenticated before making API calls
  if (!userStore.isAuthenticated) {
    await userStore.loginWithProvider('oidc_auth0', '/callback');
    return;
  }

  workspacesLoading.value = true;
  workspacesError.value = null;
  try {
    if (!workspaceStore.currentWorkspace) await workspaceStore.fetch();
    workspaceStore.currentWorkspace = selectedWorkspaceId.value
      ? selectedWorkspaceId.value
      : workspaceStore.workspaces.length > 0
        ? workspaceStore.workspaces[0]?.code
        : null;
  } catch (error) {
    console.error('Error loading workspaces:', error);
    workspacesError.value = 'Failed to load workspaces. Please try again.';
  } finally {
    workspacesLoading.value = false;
  }
};

const onWorkspaceChange = async () => {
  if (!selectedWorkspaceId.value) return;

  try {
    await assetStore.fetchForWorkspace(workspaceStore.currentWorkspace, {
      asset_type: 'CX',
      page: assetStore.pagination.page || 1,
      sort_by: assetStore.pagination.sort_by,
    });
  } catch (error) {
    console.error('Error loading assets:', error);
    assetsError.value = 'Failed to load assets. Please try again.';
    assetStore.assets = [];
    handleApiResponse(error);
  }
};

// Helper function to handle workspace initialization after loading
const handleWorkspaceInitialization = async () => {
  await loadWorkspaces();
  if (!selectedWorkspaceId.value) selectedWorkspaceId.value = workspaceStore.currentWorkspace;
  // If we have a persisted workspace ID and workspaces are loaded, auto-load assets
  if (selectedWorkspaceId.value && workspaceStore.workspaces.length > 0) {
    // Verify the workspace still exists
    const workspaceExists = workspaceStore.workspaces.some((p) => p.code === selectedWorkspaceId.value);

    if (workspaceExists) {
      await onWorkspaceChange();
    } else {
      // Clear invalid workspace ID
      selectedWorkspaceId.value = null;
    }
  }
};

// Lifecycle
onMounted(async () => {
  // Wait for auth to be checked before attempting to load workspaces
  if (userStore.isLoading) {
    // Watch for loading to complete
    const unwatch = watch(
      () => userStore.isLoading,
      async (loading) => {
        if (!loading) {
          unwatch();
          await handleWorkspaceInitialization();
        }
      }
    );
  } else {
    // Auth has already finished loading
    await handleWorkspaceInitialization();
  }
});

// Persist selected workspace to localStorage
// Use watch instead of watchEffect with immediate: false to prevent clearing
// localStorage during setup before onMounted can read it
watch(
  () => workspaceStore.currentWorkspace,
  (newWorkspace) => {
    if (newWorkspace) {
      localStorage.setItem('crux-selected-workspace-id', newWorkspace);
    } else {
      localStorage.removeItem('crux-selected-workspace-id');
    }
  },
  { immediate: false }
);
</script>
